import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import Button from "../../../../../../../../../components/shared/Button";
import Dialog from "../../../../../../../../../components/shared/Dialog";
import BaseForm, { Field } from "../../../../../../../../../components/shared/Form";
import ResourceSelect from "../../../../../../../../../components/shared/inputs/ResourceSelect";
import { useScheduleContext } from "../../../../../../hooks/context/useScheduleContext";
import { firstToUpper } from "../../../../../../../../../utils/helpers/string";
import { createSelectTemplateSchema } from "../../zodValidation";

type SelectTemplateProps = {
    submit: (_templateId: string | undefined) => Promise<boolean | void>;
    close: () => void;
    visible: boolean;
}

function SelectTemplate({ visible, close, submit }: SelectTemplateProps) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false);
    const { type } = useScheduleContext();
    const schema = useMemo(() => createSelectTemplateSchema({ t }), [t]);
    const form = useForm<{ template?: { id: string } }>({ resolver: zodResolver(schema) });

    const url = useMemo(() => {
        switch (type) {
            case "profile":
            case "my-profile":
            case "template":
            default: {
                return "/schedule-templates"
            }
        }
    }, [type]);

    const cancel = useCallback(() => {
        form.reset();
        close();
    }, [close, form]);

    const onFinish = useCallback(async (values: { template?: { id: string } }) => {
        try {
            setLoading(true);
            await submit(values.template?.id);
            form.reset();
        } catch {
            close();
        } finally {
            setLoading(false);
        }
    }, [submit, close, form]);

    return (
        <Dialog
            title={t("save-as-template")}
            open={visible}
            close={close}
        >
            <BaseForm form={form} onFinish={onFinish}>
                <Field
                    label={firstToUpper(t("template"))}
                    name="template"
                    required
                >
                    <ResourceSelect
                        resourcePath={url}
                        mode="single"
                        placeholder={firstToUpper(t("template"))}
                        labelPropName="name"
                    />
                </Field>

                <div className="tw-flex tw-items-center tw-gap-2 tw-justify-end">
                    <Button
                        variant="ghost"
                        type="button"
                        onClick={cancel}
                        disabled={loading}
                        loading={loading}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        type="submit"
                        disabled={loading}
                        loading={loading}
                    >
                        {t("save")}
                    </Button>
                </div>
            </BaseForm>
        </Dialog>
    );
}

export default SelectTemplate;
